if (!customElements.get('finch-shoppable-videos')) {
  class FinchShoppableVideos extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.track = this.querySelector('[data-fsv-track]');
      this.cards = Array.from(this.querySelectorAll('[data-fsv-card]'));
      this.previousButton = this.querySelector('[data-fsv-prev]');
      this.nextButton = this.querySelector('[data-fsv-next]');
      this.status = this.querySelector('[data-fsv-status]');
      this.videos = Array.from(this.querySelectorAll('[data-fsv-video]'));
      this.playButtons = Array.from(this.querySelectorAll('[data-fsv-play]'));
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.scrollFrame = null;

      if (!this.track || this.cards.length === 0) return;

      this.dataset.initialized = 'true';
      this.handlePreviousClick = () => this.move(-1);
      this.handleNextClick = () => this.move(1);
      this.handleScroll = this.onScroll.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handleResize = this.update.bind(this);
      this.handleBlockSelect = this.onBlockSelect.bind(this);

      this.previousButton?.addEventListener('click', this.handlePreviousClick);
      this.nextButton?.addEventListener('click', this.handleNextClick);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      this.playButtons.forEach((button) => {
        const handler = (event) => this.onPlayButtonClick(event);
        button.addEventListener('click', handler);
        button.setAttribute('aria-pressed', 'false');
        button._fsvClickHandler = handler;
      });

      this.videos.forEach((video) => {
        video.preload = 'none';
        video.controls = false;
        this.setPlayingState(video, false);
        const playHandler = () => {
          this.pauseOtherVideos(video);
          this.setPlayingState(video, true);
        };
        const pauseHandler = () => this.setPlayingState(video, false);
        const endedHandler = () => this.onVideoEnded(video);

        video.addEventListener('play', playHandler);
        video.addEventListener('pause', pauseHandler);
        video.addEventListener('ended', endedHandler);
        video._fsvHandlers = { playHandler, pauseHandler, endedHandler };
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.handleResize, { passive: true });
      }

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          (entries) => this.onVisibilityChange(entries),
          {
            root: this.track,
            threshold: [0, 0.4, 0.6, 1],
          }
        );
        this.cards.forEach((card) => this.visibilityObserver.observe(card));
      }

      requestAnimationFrame(() => this.update());
    }

    disconnectedCallback() {
      if (!this.track) return;

      this.previousButton?.removeEventListener('click', this.handlePreviousClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.removeEventListener('resize', this.handleResize);

      this.playButtons?.forEach((button) => {
        if (button._fsvClickHandler) {
          button.removeEventListener('click', button._fsvClickHandler);
          delete button._fsvClickHandler;
        }
      });

      this.videos?.forEach((video) => {
        video.pause();
        const handlers = video._fsvHandlers;
        if (!handlers) return;
        video.removeEventListener('play', handlers.playHandler);
        video.removeEventListener('pause', handlers.pauseHandler);
        video.removeEventListener('ended', handlers.endedHandler);
        delete video._fsvHandlers;
      });

      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

      delete this.dataset.initialized;
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.update();
      });
    }

    onKeydown(event) {
      if (event.target !== this.track) return;

      const keyActions = {
        ArrowLeft: () => this.move(-1),
        ArrowRight: () => this.move(1),
        Home: () => this.scrollToCard(0),
        End: () => this.scrollToCard(this.cards.length - 1),
      };
      const action = keyActions[event.key];
      if (!action) return;

      event.preventDefault();
      action();
    }

    onBlockSelect(event) {
      let card = event.target.closest?.('[data-fsv-card]');

      if (!card && event.detail?.blockId) {
        const safeBlockId = CSS.escape(String(event.detail.blockId));
        card = this.querySelector(`[data-block-id="${safeBlockId}"]`);
      }

      if (!card || !this.contains(card)) return;
      const index = this.cards.indexOf(card);
      if (index < 0) return;

      this.scrollToCard(index, 'auto');
    }

    onVisibilityChange(entries) {
      entries.forEach((entry) => {
        if (entry.intersectionRatio >= 0.4) return;
        const video = entry.target.querySelector('[data-fsv-video]');
        if (video && !video.paused) video.pause();
      });
    }

    async onPlayButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const card = event.currentTarget.closest('[data-fsv-card]');
      const video = card?.querySelector('[data-fsv-video]');
      if (!video) return;

      window.pauseAllMedia?.();
      this.pauseOtherVideos(video);
      this.loadVideo(video);
      video.controls = true;

      try {
        await video.play();
      } catch (error) {
        video.controls = false;
        this.setPlayingState(video, false);
        if (window.Shopify?.designMode) {
          console.warn('Finch shoppable video could not start.', error);
        }
      }
    }

    loadVideo(video) {
      if (video.dataset.loaded === 'true') return;

      let hasNewSource = false;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (!source.dataset.src) return;
        source.src = source.dataset.src;
        hasNewSource = true;
      });

      video.dataset.loaded = 'true';
      if (hasNewSource) video.load();
    }

    pauseOtherVideos(activeVideo) {
      this.videos.forEach((video) => {
        if (video !== activeVideo && !video.paused) video.pause();
      });
    }

    setPlayingState(video, isPlaying) {
      const card = video.closest('[data-fsv-card]');
      if (!card) return;

      card.dataset.playing = String(isPlaying);
      video.controls = isPlaying;
      card.querySelectorAll('[data-fsv-play]').forEach((button) => {
        button.setAttribute('aria-pressed', String(isPlaying));
      });
    }

    onVideoEnded(video) {
      video.currentTime = 0;
      this.setPlayingState(video, false);
    }

    move(direction) {
      const currentIndex = this.getCurrentIndex();
      const targetIndex = Math.min(
        this.cards.length - 1,
        Math.max(0, currentIndex + direction)
      );
      this.scrollToCard(targetIndex);
    }

    scrollToCard(index, requestedBehavior) {
      const card = this.cards[index];
      if (!card) return;

      const behavior =
        requestedBehavior || (this.reduceMotion.matches ? 'auto' : 'smooth');
      this.track.scrollTo({ left: card.offsetLeft, behavior });
    }

    getCurrentIndex() {
      const currentPosition = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - currentPosition);
        if (distance >= nearestDistance) return;
        nearestDistance = distance;
        nearestIndex = index;
      });

      return nearestIndex;
    }

    update() {
      if (!this.track) return;

      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const hasOverflow = maximumScroll > 1;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= maximumScroll - 1;
      const currentIndex = this.getCurrentIndex();

      this.dataset.hasOverflow = String(hasOverflow);

      if (this.previousButton) {
        this.previousButton.disabled = !hasOverflow || atStart;
        this.previousButton.setAttribute('aria-disabled', String(!hasOverflow || atStart));
      }

      if (this.nextButton) {
        this.nextButton.disabled = !hasOverflow || atEnd;
        this.nextButton.setAttribute('aria-disabled', String(!hasOverflow || atEnd));
      }

      if (this.status) {
        const itemLabel = this.dataset.itemLabel || 'Video';
        const ofLabel = this.dataset.ofLabel || '/';
        this.status.textContent = `${itemLabel} ${currentIndex + 1} ${ofLabel} ${this.cards.length}`;
      }
    }
  }

  customElements.define('finch-shoppable-videos', FinchShoppableVideos);
}
